/**
 * Lehrer-Bereich: Profile, Wortschatz, Settings, gespeicherte Texte, Korrektur.
 * Erfordert Auth.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, LogOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AuthGuard } from '@/lib/auth-guard';
import { useSession } from '@/lib/auth';
import { logoutAndWipe } from '@/lib/logout';
import { useQueryClient } from '@tanstack/react-query';
import { useSettings } from '@/lib/settings';
import { ProfileList } from '@/features/profiles/profile-list';
import { TopicEditor } from '@/features/topics/topic-editor';
import { SavedDocsList } from '@/features/documents/saved-docs-list';
import { ImportExportPanel } from '@/features/data/import-export';

export const Route = createFileRoute('/lehrer')({
  component: () => (
    <AuthGuard>
      <TeacherArea />
    </AuthGuard>
  ),
});

function TeacherArea() {
  const session = useSession();
  const settings = useSettings();
  const qc = useQueryClient();

  return (
    <div className="flex h-screen flex-col bg-sky-bottom">
      <header className="flex h-16 items-center gap-3 border-b-2 border-warning/20 bg-white px-4">
        <Button asChild variant="ghost" size="icon" aria-label="Zurück">
          <Link to="/">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-warning text-2xl">
          ⭐
        </div>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-warning">Lehrer-Bereich</h1>
          <p className="text-xs text-gray-500">
            Eingeloggt als {session.data?.user.name || session.data?.user.email}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void logoutAndWipe(qc)}>
          <LogOut className="h-4 w-4" /> Abmelden
        </Button>
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {/* Profile */}
          <Card className="md:col-span-2 lg:col-span-1">
            <CardHeader>
              <CardTitle>👤 Schüler:innen</CardTitle>
            </CardHeader>
            <CardContent>
              <ProfileList />
            </CardContent>
          </Card>

          {/* Settings */}
          <Card>
            <CardHeader>
              <CardTitle>⚙️ Einstellungen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ToggleRow
                label="Wortvorhersage"
                checked={settings.predictionsEnabled}
                onChange={(v) => settings.set('predictionsEnabled', v)}
              />
              <ToggleRow
                label="Auto-Speichern (alle paar Sekunden)"
                checked={settings.autoSave}
                onChange={(v) => settings.set('autoSave', v)}
              />
              <ToggleRow
                label="Großbuchstabe am Satzanfang"
                checked={settings.autoCapSentence}
                onChange={(v) => settings.set('autoCapSentence', v)}
              />

              <div className="space-y-2">
                <Label>Schriftgröße im Editor: {settings.fontSize}px</Label>
                <input
                  type="range"
                  min={20}
                  max={56}
                  step={2}
                  value={settings.fontSize}
                  onChange={(e) => settings.set('fontSize', Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label>Sprechgeschwindigkeit: {settings.ttsRate.toFixed(2)}×</Label>
                <input
                  type="range"
                  min={0.5}
                  max={1.5}
                  step={0.05}
                  value={settings.ttsRate}
                  onChange={(e) => settings.set('ttsRate', Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label>Sprachausgabe</Label>
                <Select
                  value={settings.voiceMode === 'elevenlabs' ? 'system' : settings.voiceMode}
                  onValueChange={(v) => settings.set('voiceMode', v as typeof settings.voiceMode)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/*
                      "Hochwertig (ElevenLabs)" ist vorerst ausgeblendet, weil noch
                      keine MP3-Files generiert wurden. Sobald die Audios da sind,
                      diese Zeile wieder aktivieren:
                      <SelectItem value="elevenlabs">Hochwertig (ElevenLabs)</SelectItem>
                    */}
                    <SelectItem value="system">System-Stimme</SelectItem>
                    <SelectItem value="off">Aus</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Wortschatz */}
          <Card>
            <CardHeader>
              <CardTitle>📚 Wortschatz / Themen</CardTitle>
            </CardHeader>
            <CardContent>
              <TopicEditor />
            </CardContent>
          </Card>

          {/* Gespeicherte Texte */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>📝 Gespeicherte Texte</CardTitle>
            </CardHeader>
            <CardContent>
              <SavedDocsList />
            </CardContent>
          </Card>

          {/* Daten */}
          <Card>
            <CardHeader>
              <CardTitle>💾 Sicherung &amp; Übernahme</CardTitle>
            </CardHeader>
            <CardContent>
              <ImportExportPanel />
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 py-2">
      <Label className="cursor-pointer flex-1">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
